import { Component, Input, Output, EventEmitter, OnChanges, OnInit, ChangeDetectionStrategy, ChangeDetectorRef } from '@angular/core';
import { ChecklistItemViewModel } from 'src/app/modules/shared/models/checklists/checklistItemViewModel';
import { FilterTypes } from 'src/app/modules/shared/enums/filterTypes';
import { Constants } from 'src/app/modules/shared/models/constants';
import { AllowedFiltersService } from 'src/app/modules/shared/services/allowed-filters.service';
import { FilterViewModel } from 'src/app/modules/shared/models/filter/filterViewModel';
import { FilterTypeSelectorViewModel } from 'src/app/modules/shared/viewModels/filters/filterTypeSelectorViewModel';
import { FilterSelectorTypes } from 'src/app/modules/shared/enums/filter/filterSelectorTypes';
import { Employee } from 'src/app/modules/shared/models/employee';
import { ObjectTypes } from 'src/app/modules/shared/enums/objectTypes';
import { AuthenticationService } from 'src/app/modules/shared/services/authentication.service';
import {
  ChecklistOptionSet1,
  ChecklistOptionSet2,
  ChecklistResponseTypes,
} from 'src/app/modules/shared/enums/checklists/checklistEnums';
import { EditableFieldTypes } from 'src/app/modules/shared/enums/editableFieldTypes';
import { FilterUtilities } from 'src/app/modules/shared/utilities/filter.utilities';
import { T } from 'src/assets/i18n/translation-keys';
import { JobItemViewModel } from 'src/app/modules/incidents/viewModels/jobs/jobItemViewModel';
import { UploadViewModel } from 'src/app/modules/shared/viewModels/documents/uploadViewModel';
import { ChecklistTypes } from 'src/app/modules/shared/enums/checklists/checklistTypes.enum';

@Component({
    selector: 'app-checklist-item-card',
    templateUrl: 'checklist-item-card.component.html',
    styleUrls: ['checklist-item-card.component.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush,
    standalone: false
})
export class ChecklistItemCardComponent implements OnInit, OnChanges {
  private mobileWidth = Constants.xs;
  private menuFocusTimeout: any;
  private announceTimeout: any;

  @Input() parentItem: JobItemViewModel;
  @Input() item: ChecklistItemViewModel;
  @Input() userHasWriteAccess: boolean;
  @Input() showOwnerLozenge = true;
  @Input() draggable = true;
  @Input() loading: boolean = false;
  @Input() checklistType: ChecklistTypes;
  @Output() itemCompleted = new EventEmitter<ChecklistItemViewModel>();
  @Output() attachmentChanged = new EventEmitter<ChecklistItemViewModel>();

  filterSettings: FilterViewModel[] = [];
  lozengesFilterTypes = [FilterTypes.Owner];
  hasNoLozenges = true;
  noBoxShadowStyle = '{"box-shadow":"none"}';
  ownerFilterTypeSelector: FilterTypeSelectorViewModel;
  employee: Employee;
  objectType = ObjectTypes.Checklist_Item;
  responseTypes = ChecklistResponseTypes;
  checklistTypes = ChecklistTypes;

  optionSet1Items = ChecklistOptionSet1.items();
  optionSet2Items = ChecklistOptionSet2.items();
  editableFieldTypes = EditableFieldTypes;
  hasOwner: boolean;
  public readonly T = T;
  showTextResponseField = false;
  objectTypes = ObjectTypes;
  attachmentsHasBeenInitialized = false;
  filterTypes = FilterTypes;
  public isMenuOpen = false;


  constructor(
    private readonly allowedFiltersService: AllowedFiltersService,
    private readonly authenticationService: AuthenticationService,
    private readonly changeDetectorRef: ChangeDetectorRef
  ) {}

  ngOnInit() {
    this.hasOwner = !!this.item.employeeId;
    this.initVisibleFilterTypes();
    this.employee = this.authenticationService.getCurrentEmployee();

    this.setTextResponseFieldVisibility();
  }

  ngOnDestroy(): void {
    if (this.menuFocusTimeout) {
      clearTimeout(this.menuFocusTimeout);
    }
    if (this.announceTimeout) {
      clearTimeout(this.announceTimeout);
    }
  }

  setTextResponseFieldVisibility() {
    this.showTextResponseField =
      this.item.responseType === this.responseTypes.Options_Set_2 || this.item.responseType === this.responseTypes.Options_Set_1;
  }

  ngOnChanges(): void {
    if (this.item) {
      this.getEmployeeSetting();
      this.setTextResponseFieldVisibility();
      this.changeDetectorRef.detectChanges();
    }
  }

  private getEmployeeSetting() {
    const settings = this.allowedFiltersService.getCachedAllowedFiltersByType(FilterTypes.Owner);
    let setting = settings.find(
      (s) => s.filterType === FilterTypes.Owner && s.filterValue.toString() === this.item.employeeId.toString()
    );
    if (!setting) {
      setting = FilterUtilities.GenerateFilter(FilterTypes.Owner, 0, 'n/a');
    }

    if (setting) {
      this.filterSettings = [setting];
      this.hasNoLozenges = false;
    }
  }

  getCardAriaLabel(): string {
    let label = this.item.title;

    if (this.item.starred) {
      label += ', required';
    }

    if (this.item.important) {
      label += ', marked important';
    }

    if (this.item.isNotApplicable) {
      label += ', marked not applicable';
    }

    return label;
  }

  getTitleId(): string {
    return `checklist-item-title-${this.item.id}`;
  }

  getDescriptionId(): string {
    return `checklist-item-desc-${this.item.id}`;
  }

  getCheckboxAriaLabel(): string {
    const state = this.item.completed ? 'checked' : 'not checked';
    return `${this.item.title}, checkbox, ${state}`;
  }

  getNumericResponseAriaLabel(): string {
     if (this.item.response && this.stripHtml(this.item.response).trim()) {
      return `${this.stripHtml(this.item.response)}. Edit response.`;
    }

    return 'No response yet. Edit response.';
  }

  getTextResponseAriaLabel(isAdditionalText: boolean = false): string {
    let label = this.item.title;

    if (this.item.response && this.stripHtml(this.item.response).trim() && !isAdditionalText) {
      return label += `, ${this.stripHtml(this.item.response)}, edit response, multi-line`;
    }

    if (this.item.textResponse && this.stripHtml(this.item.textResponse).trim() && isAdditionalText) {
      return label += `, ${this.stripHtml(this.item.textResponse)}, edit response, multi-line`;
    }

    return label += " Write a response, edit response, multi-line";
  }

  getButtonAriaLabel(optionValue: string): string {
    const isSelected = this.isOptionSelected(optionValue);
    return `${optionValue}, ${isSelected ? 'selected' : 'not selected'}`;
  }

  getMenuButtonAriaLabel(): string {
    return `Actions menu for ${this.item.title}`;
  }

  getAttachmentAriaLabel(): string {
    const count = this.item.attachmentsCount || 0;
    return `${count} ${count === 1 ? 'attachment' : 'attachments'}. Double tap to expand.`;
  }

  isOptionSelected(optionValue: string): boolean {
    const option1 = this.optionSet1Items.find(o => o.value === optionValue);
    const option2 = this.optionSet2Items.find(o => o.value === optionValue);
    const key = option1?.key || option2?.key;
    return this.item.response === key?.toString();
  }

  announceStatusChange(message: string): void {
    if (this.announceTimeout) {
      clearTimeout(this.announceTimeout);
    }

    const liveRegion = document.createElement('div');
    liveRegion.setAttribute('role', 'status');
    liveRegion.setAttribute('aria-live', 'polite');
    liveRegion.setAttribute('aria-atomic', 'true');
    liveRegion.style.position = 'absolute';
    liveRegion.style.left = '-10000px';
    liveRegion.textContent = message;
    document.body.appendChild(liveRegion);

    this.announceTimeout = setTimeout(() => {
      if (document.body.contains(liveRegion)) {
        document.body.removeChild(liveRegion);
      }
      this.announceTimeout = null;
    }, 1000);
  }

  onCheckComplete(state: string) {
    if (state === 'checked') {
      this.item.completed = true;
      this.item.response = this.item.completed.toString();
      this.itemCompleted.next(this.item);
      this.announceStatusChange('Check marked as complete');
    } else if (state === 'unchecked') {
      this.item.completed = false;
      this.item.response = this.item.completed.toString();
      this.itemCompleted.next(this.item);
      this.announceStatusChange('Check marked as incomplete');
    }
  }

  get isMobile(): boolean {
    return window.innerWidth <= this.mobileWidth;
  }

  initVisibleFilterTypes() {
    const ownerFilterType = new FilterTypeSelectorViewModel();
    ownerFilterType.filterSelectorType = FilterSelectorTypes.Dropdown;
    ownerFilterType.filterType = FilterTypes.Owner;
    ownerFilterType.filterTypeText = 'Owner';
    ownerFilterType.isSelected = true;
    ownerFilterType.viewOrder = 0;
    ownerFilterType.dateProperty = 0;

    this.ownerFilterTypeSelector = ownerFilterType;
  }

  selectType(response: { key: number; value: string }) {
    this.item.response = response.key.toString();
    this.itemCompleted.next(this.item);
    this.announceStatusChange(`Status set to ${response.value}`);
  }

  saveInputResponse($event: string) {
    const richText = $event;
    this.item.response = richText;
    this.itemCompleted.next(this.item);
  }

  saveTextResponse($event: string) {
    const richText = $event;
    this.item.textResponse = richText;
    this.itemCompleted.next(this.item);
  }

  saveNumericResponse(){
    if(this.item.response){
      this.itemCompleted.next(this.item);
    }
  }

  updateFilters(filters: FilterViewModel[]) {
    const filter = filters.find((f) => f.filterType === FilterTypes.Owner && (f.filterValue as string) !== '0');
    this.item.employeeId = filter ? parseInt(filter.filterValue as string) : 0;

    if (filter) {
      this.filterSettings = [filter];
    }

    this.itemCompleted.next(this.item);
  }

  changeApplicableState() {
    this.item.isNotApplicable = !this.item.isNotApplicable;
    this.itemCompleted.next(this.item);
    const message = this.item.isNotApplicable ?
      'Check marked as not applicable' :
      'Check marked as applicable';
    this.announceStatusChange(message);
  }

  changeImportantState() {
    this.item.important = !this.item.important;
    this.itemCompleted.next(this.item);
    const message = this.item.important ?
      'Check marked as important' :
      'Check marked as not important';
    this.announceStatusChange(message);
  }

  openAttachmentsSection() {
    this.attachmentsHasBeenInitialized = true;
  }

  onDocumentsChanged(documents: UploadViewModel[]) {
    const previousCount = this.item.attachmentsCount || 0;
    this.item.attachmentsCount = documents.length;

    if (previousCount !== documents.length) {
      this.attachmentChanged.emit(this.item);
      this.itemCompleted.next(this.item);
    }
  }

  getChecklistItemContextualInfo(): any {
    if (!this.item) {
      return null;
    }

    return {
      objectType: ObjectTypes.Checklist_Item,
      title: this.item.title,
      responseType: this.item.responseType,
      response: this.item.response,
      textResponse: this.item.textResponse
    };
  }

  onMenuButtonClick(event: Event): void {
    event.stopPropagation();
    this.isMenuOpen = !this.isMenuOpen;
    this.changeDetectorRef.detectChanges();

    if (this.isMenuOpen) {
      this.announceStatusChange('Actions menu opened');

      // Focus first menu item after a short delay
      // Clear any existing timeout first
      if (this.menuFocusTimeout) {
        clearTimeout(this.menuFocusTimeout);
      }

      this.menuFocusTimeout = setTimeout(() => {
        const firstMenuItem = document.querySelector('#actions-menu button[role="menuitem"]') as HTMLElement;
        if (firstMenuItem) {
          firstMenuItem.focus();
        }
        this.menuFocusTimeout = null;
      }, 100);
    } else {
      this.announceStatusChange('Actions menu closed');
    }
  }

  onMenuClose(): void {
    this.isMenuOpen = false;

    // Clear timeout if menu is closed before it completes
    if (this.menuFocusTimeout) {
      clearTimeout(this.menuFocusTimeout);
      this.menuFocusTimeout = null;
    }

    this.changeDetectorRef.detectChanges();
  }

  // Helper method to strip HTML tags (add if you don't have it already)
  private stripHtml(html: string): string {
    const tmp = document.createElement('DIV');
    tmp.innerHTML = html;
    return tmp.textContent || tmp.innerText || '';
  }
}
